import type { ReactNode } from 'react'
import { Linking, Text, View } from 'react-native'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { GlassSheet } from '@/components/ui/GlassSheet'
import { Pressable } from '@/components/ui/Pressable'
import { Button } from '@/components/ui/Button'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, SPACING, INTERACTION } from '@/lib/theme/tokens'
import { FONTS } from '@/lib/brand'
import type { RentalTermsVersionInfo } from '@/lib/services/legal.service'
import { toast } from '@/lib/toast'

type Props = {
  terms: RentalTermsVersionInfo | null
  loading?: boolean
  onRetry?: () => void
  children?: ReactNode
  showPaymentDisclosure?: boolean
}

export function RentalTermsDocuments({
  terms,
  loading,
  onRetry,
  children,
  showPaymentDisclosure = false,
}: Props) {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const open = async (url: string, slug: 'terms-of-service' | 'cancellation-policy') => {
    try {
      if (terms?.version === '1.3') {
        router.push(`/legal/${slug}` as never)
        return
      }
      await Linking.openURL(url)
    } catch {
      toast.error(t('rental_terms_flow.open_error'))
    }
  }

  return (
    <GlassSheet
      testID="rental-terms.documents"
      contentStyle={{ padding: SPACING.md, gap: SPACING.sm }}
    >
      <Text style={{ color: colors.textPrimary, fontFamily: FONTS.bodyBold, fontSize: 16 }}>
        {terms
          ? t('rental_terms_flow.title', { version: terms.version })
          : t('rental_terms_flow.loading_title')}
      </Text>
      {terms ? (
        <>
          <Text
            style={{
              color: colors.textSecondary,
              fontFamily: FONTS.body,
              fontSize: 13,
              lineHeight: 19,
            }}
          >
            {i18n.language.startsWith('en') ? terms.summaryEn : terms.summaryEs}
          </Text>
          {showPaymentDisclosure && (
            <Text
              testID="rental-terms.paid-request-disclosure"
              style={{
                color: colors.textPrimary,
                fontFamily: FONTS.body,
                fontSize: 14,
                lineHeight: 21,
              }}
            >
              {t('rental_terms_flow.payment_disclosure')}
            </Text>
          )}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.md }}>
            <Pressable
              accessibilityRole="link"
              testID="rental-terms.open-terms"
              onPress={() => void open(terms.termsUrl, 'terms-of-service')}
              style={{ minHeight: INTERACTION.minTouchTarget, justifyContent: 'center' }}
            >
              <Text style={{ color: colors.textAccent, fontFamily: FONTS.bodyBold, fontSize: 13 }}>
                {t('rental_terms_flow.read_terms')}
              </Text>
            </Pressable>
            <Pressable
              accessibilityRole="link"
              testID="rental-terms.open-cancellation"
              onPress={() => void open(terms.cancellationUrl, 'cancellation-policy')}
              style={{ minHeight: INTERACTION.minTouchTarget, justifyContent: 'center' }}
            >
              <Text style={{ color: colors.textAccent, fontFamily: FONTS.bodyBold, fontSize: 13 }}>
                {t('rental_terms_flow.read_cancellation')}
              </Text>
            </Pressable>
          </View>
          {children}
        </>
      ) : (
        <>
          <Text style={{ color: colors.textSecondary, fontFamily: FONTS.body, fontSize: 13 }}>
            {t(loading ? 'rental_terms_flow.loading' : 'rental_terms_flow.unavailable')}
          </Text>
          {!loading && onRetry && (
            <Button
              title={t('rental_terms_flow.retry')}
              variant="secondary"
              size="sm"
              onPress={onRetry}
            />
          )}
        </>
      )}
    </GlassSheet>
  )
}
